import React, { useState, useRef, useEffect } from 'react';
import { Minus, Plus, X, ShoppingBag, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart, cartUtils } from '../contexts/CartContext';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'motion/react';

// Design system colors
const colors = {
  dark: '#183028',
  darkLight: '#234a3a',
  accent: '#64a70b',
  accentHover: '#578f09',
  neutral: '#c1c6c8',
  neutralLight: '#e8eaeb',
  white: '#ffffff',
  textDark: '#333333',
  textMuted: '#666666',
};

interface OrderDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const OrderDrawer: React.FC<OrderDrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const {
    cart,
    removeFromCart,
    incrementQuantity,
    decrementQuantity,
    subtotal,
    tax,
    shipping,
    total,
  } = useCart();
  const sheetRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleBrowseProducts = () => {
    onClose();
    navigate('/clothing');
  };

  // The quote flow is its own set of screens at /quote now, not a modal over
  // the shop, so the drawer hands off and closes.
  const handleStartOrder = () => {
    onClose();
    navigate('/quote');
  };

  if (!isOpen) return null;

  // Mobile bottom sheet design
  if (isMobile) {
    return (
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 z-40"
              onClick={onClose}
            />

            {/* Bottom Sheet */}
            <motion.div
              ref={sheetRef}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              onDragEnd={(e, info) => {
                if (info.offset.y > 100 || info.velocity.y > 500) onClose();
              }}
              className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl overflow-hidden"
              style={{
                backgroundColor: colors.dark,
                maxHeight: '95vh',
              }}
            >
              {/* Drag Handle - tappable to close */}
              <div className="flex justify-center pt-3 pb-2 cursor-pointer" onClick={onClose}>
                <div className="w-10 h-1 rounded-full bg-white/30" />
              </div>

              {(
                <>
                  {/* Header */}
                  <div className="px-5 pb-4 border-b border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <ShoppingBag className="w-6 h-6" style={{ color: colors.accent }} />
                        <h2 className="text-xl font-bold text-white">
                          Your Quote Request
                        </h2>
                        {cart.length > 0 && (
                          <span className="text-sm font-normal text-white/60">
                            ({cart.length} {cart.length === 1 ? 'item' : 'items'})
                          </span>
                        )}
                      </div>
                      <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5 text-white/60" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto px-5 py-4" style={{ maxHeight: 'calc(90vh - 200px)' }}>
                    {cart.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <ShoppingBag className="w-16 h-16 text-white/30 mb-4" />
                        <p className="text-lg font-medium text-white mb-2">
                          You have not yet started a quote
                        </p>
                        <p className="text-sm text-white/60 mb-6">
                          Browse our products to add them to your quote
                        </p>
                        <button
                          onClick={handleBrowseProducts}
                          className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white transition-all"
                          style={{ backgroundColor: colors.accent }}
                        >
                          Browse Products
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {cart.map((item) => (
                          <CartItemCard
                            key={item.id}
                            item={item}
                            onRemove={() => removeFromCart(item.id)}
                            onIncrement={() => incrementQuantity(item.id)}
                            onDecrement={() => decrementQuantity(item.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  {cart.length > 0 && (
                    <div className="px-5 py-4 border-t border-white/10" style={{ backgroundColor: colors.dark }}>
                      {/* Totals */}
                      <div className="space-y-1.5 text-sm mb-4">
                        <div className="flex justify-between text-white/70">
                          <span>Quote Amount</span>
                          <span>{cartUtils.formatPrice(subtotal)}</span>
                        </div>
                        <div className="flex justify-between text-white/70">
                          <span>VAT (20%)</span>
                          <span>{cartUtils.formatPrice(tax)}</span>
                        </div>
                        <div className="border-t border-white/10 pt-2 mt-2" />
                        <p className="text-xs text-white/40 leading-relaxed">
                          *Please note: Your final quote total may vary depending on quantities, customisation costs and shipping.
                        </p>
                      </div>

                      {/* Submission info */}
                      <p className="text-xs text-white/50 leading-relaxed mb-3">
                        Upon submission of your quote request, one of our team will contact you to finalise your quote. Customisation requirements will be discussed at this time. No payment will be due until your quote is fully confirmed.
                      </p>

                      {/* CTAs */}
                      <div className="space-y-2">
                        <button
                          onClick={handleStartOrder}
                          className="w-full py-3 rounded-lg font-semibold text-white transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                          style={{ backgroundColor: colors.accent }}
                        >
                          <Send className="w-4 h-4" />
                          Submit Quote Request
                        </button>
                        <button
                          onClick={handleBrowseProducts}
                          className="w-full py-2.5 rounded-lg font-medium text-white/80 border border-white/20 transition-all active:scale-[0.98]"
                        >
                          Add More Products
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }

  // Desktop drawer design
  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="fixed right-0 top-0 bottom-0 w-full sm:max-w-lg z-50 flex flex-col shadow-2xl overflow-hidden"
        style={{ backgroundColor: colors.dark }}
      >
        {/* Background texture */}
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: 'url(/BlackTextureBackground.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        {/* Header */}
        <div className="relative z-10 border-b border-white/10 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-6 h-6" style={{ color: colors.accent }} />
              <h2 className="hearns-font text-2xl text-white">
                Your Quote Request
              </h2>
              {cart.length > 0 && (
                <span className="neuzeit-font text-sm font-normal text-white/50">
                  ({cart.length} {cart.length === 1 ? 'item' : 'items'})
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>
        </div>

        {/* Cart Items - Scrollable */}
        <div className="relative z-10 flex-1 overflow-y-auto p-6 space-y-4 scrollbar-clothing">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                style={{ backgroundColor: `${colors.accent}20` }}
              >
                <ShoppingBag className="w-10 h-10" style={{ color: colors.accent }} />
              </div>
              <p className="hearns-font text-xl text-white mb-2">
                You have not yet started a quote
              </p>
              <p className="neuzeit-light-font text-sm text-white/60 mb-6">
                Browse our products to add them to your quote
              </p>
              <button
                onClick={handleBrowseProducts}
                className="neuzeit-font font-semibold text-white px-6 py-3 rounded-xl transition-all hover:opacity-90"
                style={{ backgroundColor: colors.accent }}
              >
                Browse Products
              </button>
            </div>
          ) : (
            cart.map((item) => (
              <CartItemCard
                key={item.id}
                item={item}
                onRemove={() => removeFromCart(item.id)}
                onIncrement={() => incrementQuantity(item.id)}
                onDecrement={() => decrementQuantity(item.id)}
              />
            ))
          )}
        </div>

        {/* Footer with totals and CTAs */}
        {cart.length > 0 && (
          <div
            className="relative z-10 border-t border-white/10 p-6 space-y-4"
            style={{ backgroundColor: colors.dark }}
          >
            {/* Totals */}
            <div className="space-y-2 text-sm neuzeit-font">
              <div className="flex justify-between text-white/70">
                <span>Quote Amount</span>
                <span>{cartUtils.formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-white/70">
                <span>VAT (20%)</span>
                <span>{cartUtils.formatPrice(tax)}</span>
              </div>
              <div className="border-t border-white/10 pt-2 mt-2" />
              <p className="text-xs text-white/40 leading-relaxed">
                *Please note: Your final quote total may vary depending on quantities, customisation costs and shipping.
              </p>
            </div>

            {/* Submission info */}
            <p className="neuzeit-light-font text-xs text-white/50 leading-relaxed">
              Upon submission of your quote request, one of our team will contact you to finalise your quote. Customisation requirements will be discussed at this time. No payment will be due until your quote is fully confirmed.
            </p>

            {/* CTAs */}
            <div className="space-y-3">
              <button
                onClick={handleStartOrder}
                className="w-full py-3.5 rounded-xl neuzeit-font font-semibold text-white transition-all hover:opacity-90 flex items-center justify-center gap-2"
                style={{
                  backgroundColor: colors.accent,
                  boxShadow: `0 6px 20px ${colors.accent}40`,
                }}
              >
                <Send className="w-4 h-4" />
                Submit Quote Request
              </button>
              <button
                onClick={handleBrowseProducts}
                className="w-full py-3 rounded-xl neuzeit-font font-medium text-white/80 border border-white/20 transition-all hover:bg-white/5"
              >
                Add More Products
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </>
  );
};

interface CartItemCardProps {
  item: {
    id: string;
    name: string;
    brand: string;
    price: number;
    quantity: number;
    image: string;
    selectedColor?: string;
    selectedSize?: string;
  };
  onRemove: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
}

const CartItemCard: React.FC<CartItemCardProps> = ({ item, onRemove, onIncrement, onDecrement }) => {
  return (
    <div className="flex gap-4 p-4 rounded-[12px] bg-white/5 border border-white/10 transition-all hover:border-white/20">
      {/* Product Image */}
      <div className="w-20 h-20 rounded-[10px] overflow-hidden bg-white flex-shrink-0">
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-contain p-1"
            onError={(e) => {
              e.currentTarget.src = `https://via.placeholder.com/80x80/183028/64a70b?text=${encodeURIComponent(item.name.slice(0, 2))}`;
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: colors.darkLight }}>
            <ShoppingBag className="w-8 h-8 text-white/40" />
          </div>
        )}
      </div>

      {/* Product Details */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start gap-2 mb-1">
          <div className="flex-1 min-w-0">
            <h3 className="neuzeit-font font-medium text-white text-sm truncate">
              {item.name}
            </h3>
            <p className="neuzeit-light-font text-xs text-white/50">
              {item.brand}
              {item.selectedColor && ` • ${item.selectedColor}`}
              {item.selectedSize && ` • ${item.selectedSize}`}
            </p>
          </div>
          <button
            onClick={onRemove}
            className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors group flex-shrink-0"
            aria-label="Remove item"
          >
            <X className="w-4 h-4 text-white/40 group-hover:text-red-400 transition-colors" />
          </button>
        </div>

        {/* Price and Quantity */}
        <div className="flex items-center justify-between mt-3">
          <span className="neuzeit-font font-semibold text-white">
            {cartUtils.formatPrice(item.price)}
          </span>

          {/* Quantity Controls */}
          <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
            <button
              onClick={onDecrement}
              className="p-1.5 rounded-md transition-colors hover:bg-white/10"
              aria-label="Decrease quantity"
            >
              <Minus className="w-3 h-3 text-white/70" />
            </button>
            <span className="w-8 text-center text-sm font-medium text-white neuzeit-font">
              {item.quantity}
            </span>
            <button
              onClick={onIncrement}
              className="p-1.5 rounded-md transition-colors"
              style={{ backgroundColor: `${colors.accent}30` }}
              aria-label="Increase quantity"
            >
              <Plus className="w-3 h-3" style={{ color: colors.accent }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDrawer;
